import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { EllipsisVertical, Plus, Copy } from 'lucide-react';
import { useQuickReplies, type QuickReply } from '@/hooks/useQuickReplies';
import { Checkbox } from '../ui/checkbox';
import ScrollArea from '../ui/shared/ScrollArea';

const QuickRepliesTab: React.FC = () => {
  const { replies, loading, createReply, updateReply, deleteReply } = useQuickReplies();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingReply, setEditingReply] = useState<QuickReply | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    content: '',
    shortcut: '',
    category: '',
    is_active: true
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const dataToSubmit = {
      ...formData,
      shortcut: formData.shortcut || null,
      category: formData.category || null
    };

    if (editingReply) {
      const success = await updateReply(editingReply.id, dataToSubmit);
      if (success) {
        setDialogOpen(false);
        resetForm();
      }
    } else {
      const created = await createReply(dataToSubmit);
      if (created) {
        setDialogOpen(false);
        resetForm();
      }
    }
  };

  const handleEdit = (reply: QuickReply) => {
    setEditingReply(reply);
    setFormData({
      name: reply.name,
      content: reply.content,
      shortcut: reply.shortcut || '',
      category: reply.category || '',
      is_active: reply.is_active
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Tem certeza que deseja excluir esta resposta rápida?')) {
      await deleteReply(id);
    }
  };

  const handleCopy = (content: string) => {
    navigator.clipboard.writeText(content);
  };

  const resetForm = () => {
    setEditingReply(null);
    setFormData({
      name: '',
      content: '',
      shortcut: '',
      category: '',
      is_active: true
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-neutral-50">Respostas Rápidas</h2>
          <p className="text-sm text-neutral-400">Crie modelos de mensagens para agilizar o atendimento</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#E4E4E7] text-neutral-900 hover:bg-[#D4D4D8]">
              <Plus className="w-4 h-4 mr-2" />
              Nova Resposta
            </Button>
          </DialogTrigger>
          <DialogContent className="flex w-full p-0 border-[#3F3F464D] bg-[#18181B] flex-col max-w-[500px]">
            <ScrollArea classNames={{ root: 'max-h-[90dvh] p-5 overflow-auto' }}>
              <DialogHeader>
                <DialogTitle className="text-white">
                  {editingReply ? 'Editar Resposta Rápida' : 'Nova Resposta Rápida'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="w-full mt-4 gap-4 flex flex-col">
                <div className="flex flex-col w-full items-start gap-3">
                  <Label htmlFor="name">Nome</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: Saudação inicial"
                    className="bg-neutral-600 text-[14px] placeholder:text-[#71717A] text-neutral-50 min-h-[36px] border focus:ring-white border-[#3F3F46]"
                    required
                  />
                </div>

                <div className="flex flex-col w-full items-start gap-3">
                  <Label htmlFor="content">Mensagem</Label>
                  <Textarea
                    id="content"
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder="Digite a mensagem..."
                    className="bg-neutral-600 text-[14px] placeholder:text-[#71717A] text-neutral-50 min-h-[100px] border focus:ring-white border-[#3F3F46]"
                    required
                  />
                </div>

                <div className="flex flex-col w-full items-start gap-3">
                  <Label htmlFor="shortcut">Atalho (opcional)</Label>
                  <Input
                    id="shortcut"
                    value={formData.shortcut}
                    onChange={(e) => setFormData({ ...formData, shortcut: e.target.value })}
                    placeholder="Ex: /oi"
                    className="bg-neutral-600 text-[14px] placeholder:text-[#71717A] text-neutral-50 min-h-[36px] border focus:ring-white border-[#3F3F46]"
                  />
                  <p className="text-xs text-neutral-500">Use / no início para criar um atalho rápido</p>
                </div>

                <div className="flex flex-col w-full items-start gap-3">
                  <Label htmlFor="category">Categoria (opcional)</Label>
                  <Input
                    id="category"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    placeholder="Ex: Vendas, Suporte"
                    className="bg-neutral-600 text-[14px] placeholder:text-[#71717A] text-neutral-50 min-h-[36px] border focus:ring-white border-[#3F3F46]"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: !!checked })}
                  />
                  <Label htmlFor="is_active" className="cursor-pointer">Resposta ativa</Label>
                </div>

                <div className="flex gap-2 justify-end mt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-[#E4E4E7] text-neutral-900">
                    {editingReply ? 'Salvar' : 'Criar'}
                  </Button>
                </div>
              </form>
            </ScrollArea>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-[#3F3F464D] rounded-lg bg-[#18181B]">
        {loading ? (
          <div className="p-8 text-center text-neutral-400">Carregando...</div>
        ) : replies.length === 0 ? (
          <div className="p-8 text-center text-neutral-400">Nenhuma resposta rápida criada</div>
        ) : (
          <div className="divide-y divide-[#3F3F464D]">
            {replies.map((reply) => (
              <div key={reply.id} className="p-4 hover:bg-neutral-800/50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-medium text-neutral-50">{reply.name}</h3>
                      {reply.shortcut && (
                        <span className="text-xs px-2 py-1 rounded bg-blue-900/30 text-blue-400 font-mono">
                          {reply.shortcut}
                        </span>
                      )}
                      {reply.category && (
                        <span className="text-xs px-2 py-1 rounded bg-neutral-700 text-neutral-300">
                          {reply.category}
                        </span>
                      )}
                      {!reply.is_active && (
                        <span className="text-xs px-2 py-1 rounded bg-red-900/30 text-red-500">
                          Inativa
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-neutral-400 whitespace-pre-wrap">{reply.content}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleCopy(reply.content)}
                      className="flex justify-center items-center rounded-[8px] border-[1px] border-[#3F3F464D] bg-neutral-600 w-[34px] h-[34px] hover:bg-neutral-500"
                      title="Copiar mensagem"
                    >
                      <Copy size={16} color="#FAFAFA" />
                    </button>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button className="flex justify-center items-center rounded-[8px] border-[1px] border-[#3F3F464D] bg-neutral-600 w-[34px] h-[34px]">
                          <EllipsisVertical size={20} color="#FAFAFA" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="!bg-[#18181B] p-1 w-[95px] flex flex-col gap-1 border-[#3F3F464D] border">
                        <button
                          onClick={() => handleEdit(reply)}
                          className="w-full flex justify-center rounded-[7px] items-center bg-transparent hover:bg-[#27272A] min-h-[29px]"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleDelete(reply.id)}
                          className="w-full flex justify-center rounded-[7px] items-center bg-transparent hover:bg-[#27272A] min-h-[29px] text-red-400"
                        >
                          Excluir
                        </button>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuickRepliesTab;
